import React from 'react';
import { Clock } from 'lucide-react';
import { TimeSlot, BookingConfig } from '../../types/booking';

interface TimeSlotPickerProps {
  timeSlots: TimeSlot[];
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  config: BookingConfig;
  error?: string;
}

export const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  timeSlots,
  selectedTime,
  onTimeSelect,
  config,
  error
}) => {
  const { theme, darkMode } = config;

  if (timeSlots.length === 0) {
    return (
      <div className={`p-4 rounded-lg border-2 border-dashed text-center ${
        darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
      }`}>
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No available time slots for this date</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {timeSlots.map((slot) => (
          <button
            key={slot.time}
            type="button"
            disabled={!slot.available}
            onClick={() => onTimeSelect(slot.time)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
              selectedTime === slot.time
                ? `text-white shadow-md`
                : slot.available
                ? `border hover:shadow-sm ${
                    darkMode
                      ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`
                : `opacity-50 cursor-not-allowed ${
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-gray-500'
                      : 'bg-gray-100 border-gray-200 text-gray-400'
                  }`
            }`}
            style={{
              backgroundColor: selectedTime === slot.time ? theme.primary : undefined,
              borderColor: selectedTime === slot.time ? theme.primary : theme.border,
              '--focus-ring-color': theme.primary,
            } as React.CSSProperties}
            aria-pressed={selectedTime === slot.time}
            aria-disabled={!slot.available}
          >
            {slot.time}
          </button>
        ))}
      </div>
      
      {error && (
        <p 
          className="text-sm flex items-center gap-1"
          style={{ color: theme.error }}
          role="alert"
          aria-live="polite"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};